import { PageAnchor } from 'src/app/AppAuth/PageAnchor'
import React, { ReactNode, useCallback, useRef, useState } from 'react'
import styled from 'styled-components/macro'
import OverlayTrigger, {
  OverlayDelay,
  OverlayTriggerProps,
} from 'react-bootstrap/OverlayTrigger'
import BootstrapTooltip from 'react-bootstrap/Tooltip'
import Popover from 'react-bootstrap/Popover'
import palette from 'src/designSystem/variables/palette'
import { Markup } from '../Text/Markup'

const StyledPopover = styled(Popover)<{
  hideArrow?: boolean
  $compact?: boolean
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}>`
  max-width: ${(props) => (props.$compact ? '220px' : '550px')};
  .popover-body {
    background-color: ${palette.neutral.black};
    color: ${palette.neutral.white};
    border-radius: 4px;
    ${(props) => props.$compact && `font-size: 12px;`}
  }

  ${(props) =>
    props.hideArrow &&
    `
    .popover-arrow {
      display: none;
    }
  `}

  ${(props) =>
    !props.hideArrow &&
    props.placement === 'top' &&
    `
    .popover-arrow::after {
      border-top-color: ${palette.neutral.black};
    }
  `}

  ${(props) =>
    !props.hideArrow &&
    props.placement === 'bottom' &&
    `
    .popover-arrow::after {
      border-bottom-color: ${palette.neutral.black};
    }
  `}

  ${(props) =>
    !props.hideArrow &&
    props.placement === 'left' &&
    `
    .popover-arrow::after {
      border-left-color: ${palette.neutral.black};
    }
  `}

  ${(props) =>
    !props.hideArrow &&
    props.placement === 'right' &&
    `
    .popover-arrow::after {
      border-right-color: ${palette.neutral.black};
    }
  `}
`

const TooltipMarkdownContent = styled.div`
  max-width: 400px;
  font-size: 13px;
  line-height: 1.5;

  p {
    margin: 0 0 8px;
    &:last-child {
      margin-bottom: 0;
    }
  }

  a {
    color: ${palette.blue[100]};
  }
`

const TooltipLearnMoreLink = styled(PageAnchor)`
  display: inline-block;
  margin-top: 8px;
  margin-bottom: 8px;
  color: ${palette.blue[100]};
  font-weight: 500;
  text-decoration: none;

  &:hover,
  &:visited {
    color: ${palette.blue[100]};
  }
`

const defaultTooltipDelay: OverlayDelay = { show: 150, hide: 150 }

// More options: https://mui.com/material-ui/api/tooltip/#props
export enum TooltipPositions {
  default = 'auto',
  top = 'top',
  bottom = 'bottom',
  left = 'left',
  right = 'right',
}

export enum TooltipOverlayType {
  tooltip = 'tooltip',
  popover = 'popover',
}

export enum TOOLTIP_TRIGGERS {
  HOVER = 'hover',
  FOCUS = 'focus',
}

export const Tooltip = ({
  id,
  trigger,
  defaultShow,
  show,
  onToggle,
  delay = defaultTooltipDelay,
  placement = TooltipPositions.default,
  overlayType = TooltipOverlayType.tooltip,
  tooltipBody,
  children,
  disabled = false,
  width,
  hideArrow = false,
  compact = false,
  keepOpenOnHover: keepOpenOnHoverProp = false,
  markdown,
  learnMoreHref,
}: Omit<OverlayTriggerProps, 'overlay'> & {
  id: string
  overlayType?: TooltipOverlayType
  tooltipBody: ReactNode
  disabled?: boolean
  width?: number
  hideArrow?: boolean
  compact?: boolean
  keepOpenOnHover?: boolean
  markdown?: string
  learnMoreHref?: string
}): JSX.Element => {
  // Auto-enable keepOpenOnHover when learnMoreHref is provided
  const keepOpenOnHover = keepOpenOnHoverProp || !!learnMoreHref

  const resolvedTooltipBody =
    markdown || learnMoreHref ? (
      <TooltipMarkdownContent>
        {markdown ? <Markup markdown={markdown} /> : tooltipBody}
        {(markdown || tooltipBody) && learnMoreHref && ' '}
        {learnMoreHref && (
          <TooltipLearnMoreLink
            href={learnMoreHref}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            Learn more
          </TooltipLearnMoreLink>
        )}
      </TooltipMarkdownContent>
    ) : (
      tooltipBody
    )
  const [internalShow, setInternalShow] = useState(false)
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
  }, [])

  const handleMouseEnter = useCallback(() => {
    if (keepOpenOnHover) {
      clearHideTimeout()
    }
  }, [keepOpenOnHover, clearHideTimeout])

  const handleMouseLeave = useCallback(() => {
    if (keepOpenOnHover) {
      hideTimeoutRef.current = setTimeout(() => {
        setInternalShow(false)
      }, 150)
    }
  }, [keepOpenOnHover])

  const handleTooltipMouseEnter = useCallback(() => {
    if (keepOpenOnHover) {
      clearHideTimeout()
    }
  }, [keepOpenOnHover, clearHideTimeout])

  const handleTooltipMouseLeave = useCallback(() => {
    if (keepOpenOnHover) {
      setInternalShow(false)
    }
  }, [keepOpenOnHover])

  const handleToggle = useCallback(
    (nextShow: boolean) => {
      if (keepOpenOnHover) {
        if (nextShow) {
          clearHideTimeout()
          setInternalShow(true)
        }
        // Don't hide on toggle(false) — let mouse leave handlers manage it
      }
      onToggle?.(nextShow)
    },
    [keepOpenOnHover, clearHideTimeout, onToggle],
  )

  const effectiveShow = keepOpenOnHover ? internalShow : show

  const overlay = disabled ? (
    <></>
  ) : overlayType === TooltipOverlayType.tooltip ? (
    <BootstrapTooltip
      id={id}
      style={{ width: width }}
      onMouseEnter={handleTooltipMouseEnter}
      onMouseLeave={handleTooltipMouseLeave}
    >
      {resolvedTooltipBody}
    </BootstrapTooltip>
  ) : (
    <StyledPopover
      id={id}
      style={{ width: width }}
      hideArrow={hideArrow}
      $compact={compact}
      onMouseEnter={handleTooltipMouseEnter}
      onMouseLeave={handleTooltipMouseLeave}
    >
      <Popover.Body>{resolvedTooltipBody}</Popover.Body>
    </StyledPopover>
  )

  return (
    <OverlayTrigger
      popperConfig={{
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 10],
            },
          },
        ],
      }}
      trigger={trigger}
      defaultShow={defaultShow}
      show={effectiveShow}
      onToggle={handleToggle}
      delay={delay}
      placement={placement}
      overlay={overlay}
    >
      {keepOpenOnHover
        ? React.cloneElement(children as React.ReactElement, {
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave,
          })
        : children}
    </OverlayTrigger>
  )
}
